import { useEffect, useRef, useState } from 'react'

/**
 * Sections that have already revealed this session. Keyed reveals survive
 * unmount/remount (e.g. navigating away and back), so entrance animations
 * play exactly once per session instead of every time the page mounts.
 */
const revealedKeys = new Set<string>()

/**
 * Fires once when the element enters the viewport.
 * Disconnects the observer after first trigger — animations don't repeat.
 *
 * Pass a unique `onceKey` to also persist the revealed state across remounts:
 * a keyed element that has already revealed starts visible immediately.
 */
export function useInView<T extends Element = HTMLDivElement>(
  threshold = 0.15,
  onceKey?: string,
) {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(() =>
    onceKey ? revealedKeys.has(onceKey) : false,
  )

  useEffect(() => {
    if (inView) return
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          setInView(true)
          if (onceKey) revealedKeys.add(onceKey)
          observer.disconnect()
        }
      },
      { threshold },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold, onceKey, inView])

  return { ref, inView }
}
